/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 */
import React, { useMemo } from 'react';
import { Text, View } from 'react-native';
import { DenseListCard } from '../../../shared/surface-cards/DenseListCard';
import { styles } from '../ReportsScreen.styles';
import { flattenPersonalSignals } from '../../domain/personalSignalRegistry'
import type { PersonalDashboardDTO, PersonalSignal, PersonalSignalSeverity } from '../../domain/reportsPersonalTypes';
import { ticketColors } from '../../../shared/theme/ticketColors';

type Props = {
  dashboard: PersonalDashboardDTO | null;
  layoutWide?: boolean;
};

const SIGNAL_CATEGORY: Record<string, string> = {
  cash_shortage: 'Caja',
  cash_gap: 'Caja',
  low_ticket_vs_team: 'Ventas',
  ticket_below_own_history: 'Ventas',
  pending_payments: 'Ventas',
  stock_no_sales: 'Stock',
  high_shrinkage: 'Stock',
  high_fiado_balance: 'Clientes',
  high_voided_sales: 'Control',
  no_cashier: 'Control',
  cashier_mismatch: 'Control',
  high_custom_items: 'Control',
};

const CATEGORY_ORDER = ['Caja', 'Ventas', 'Stock', 'Clientes', 'Control'];

function signalKey(signal: PersonalSignal): string {
  return `${signal.code}-${signal.operatorUserId ?? 'branch'}`;
}

function severityAccent(severity: PersonalSignalSeverity): string {
  if (severity === 'priority') return ticketColors.bad;
  if (severity === 'review') return ticketColors.gold;
  return ticketColors.rule;
}

const SEVERITY_RANK: Record<PersonalSignalSeverity, number> = {
  priority: 0,
  review: 1,
  info: 2,
};

function sortSignalsBySeverity(signals: PersonalSignal[]): PersonalSignal[] {
  return [...signals].sort((a, b) => SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity]);
}

function PersonalSignalCard({
  signal,
  operatorLabel,
  layoutWide,
}: {
  signal: PersonalSignal;
  operatorLabel: string | null;
  layoutWide?: boolean;
}) {
  const scopeLabel = operatorLabel ?? 'Sucursal';

  return (
    <DenseListCard
      wrapStyle={layoutWide ? styles.personalSignalCardWide : undefined}
      bodyStyle={styles.personalSignalCardBody}
      accentBarColor={severityAccent(signal.severity)}
    >
      <Text style={styles.reportsDenseListTitle} numberOfLines={2}>
        {signal.title}
      </Text>
      <Text style={styles.reportsDenseListMeta} numberOfLines={1}>
        {scopeLabel}
      </Text>
      <Text style={styles.personalSignalDetail} numberOfLines={layoutWide ? 2 : 3}>
        {signal.detail}
      </Text>
    </DenseListCard>
  );
}

export function ReportsPersonalSignalsSection({ dashboard, layoutWide }: Props) {
  const operatorNames = useMemo(() => {
    const map = new Map<string, string>();
    if (!dashboard) return map;
    for (const op of dashboard.facts.operators) {
      if (op.cashierUserId) map.set(op.cashierUserId, op.displayName);
    }
    return map;
  }, [dashboard]);

  const groupedSignals = useMemo(() => {
    if (!dashboard) return [];
    const signals = sortSignalsBySeverity(
      flattenPersonalSignals(dashboard.branchSignals, dashboard.operatorSignals)
    );
    const buckets = new Map<string, PersonalSignal[]>();
    for (const s of signals) {
      const cat = SIGNAL_CATEGORY[s.code] ?? 'Control';
      const list = buckets.get(cat) ?? [];
      list.push(s);
      buckets.set(cat, list);
    }
    return CATEGORY_ORDER.filter((c) => (buckets.get(c)?.length ?? 0) > 0).map((category) => ({
      category,
      signals: buckets.get(category) ?? [],
    }));
  }, [dashboard]);

  if (!dashboard) {
    return (
      <View style={styles.personalSignalsSection}>
        <DenseListCard>
          <Text style={styles.reportsDenseBlockTitle}>Para mirar</Text>
          <Text style={styles.personalSignalsEmptyCopy}>
            Conectate y recargá para ver qué conviene revisar del equipo.
          </Text>
        </DenseListCard>
      </View>
    );
  }

  const resolveOperatorLabel = (signal: PersonalSignal): string | null => {
    if (!signal.operatorUserId) return null;
    return operatorNames.get(signal.operatorUserId) ?? null;
  };

  const totalCount = groupedSignals.reduce((n, g) => n + g.signals.length, 0);

  return (
    <View style={styles.personalSignalsSection}>
      <Text style={styles.reportsDenseBlockTitle}>Para mirar</Text>

      {totalCount === 0 ? (
        <Text style={styles.personalSignalsEmptyCopy}>
          Nada para mirar en este período con esos filtros.
        </Text>
      ) : (
        groupedSignals.map(({ category, signals }) => (
          <View key={category} style={{ marginTop: 8 }}>
            <Text style={[styles.caption, { fontWeight: '600', marginBottom: 4 }]}>{category}</Text>
            <View style={[styles.personalSignalList, layoutWide && styles.personalSignalListWide]}>
              {signals.map((signal) => (
                <PersonalSignalCard
                  key={signalKey(signal)}
                  signal={signal}
                  operatorLabel={resolveOperatorLabel(signal)}
                  layoutWide={layoutWide}
                />
              ))}
            </View>
          </View>
        ))
      )}

      <Text style={styles.personalSignalsFootnote}>
        Indicadores orientativos; no prueban fraude. Conviene cruzar con arqueo, inventario y cámaras.
      </Text>
    </View>
  );
}
